import { makeStyles, mergeClasses, tokens, Text } from '@fluentui/react-components';
import { MOBILE_BREAKPOINT } from './constants';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  section: {
    paddingBlock: '100px',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '64px',
    },
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      gridTemplateColumns: '1fr',
      gap: '40px',
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  // Visual on the left, copy on the right on desktop. Stack copy first on mobile.
  reverseCopy: {
    [`@media (min-width: calc(${MOBILE_BREAKPOINT} + 1px))`]: {
      gridColumnStart: 2,
      gridRowStart: 1,
    },
  },
  reverseVisual: {
    [`@media (min-width: calc(${MOBILE_BREAKPOINT} + 1px))`]: {
      gridColumnStart: 1,
      gridRowStart: 1,
    },
  },
  eyebrow: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ottoTokens.landingAccentTextCool,
    marginBlockEnd: tokens.spacingVerticalM,
  },
  heading: {
    display: 'block',
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
    marginBlockEnd: tokens.spacingVerticalL,
  },
  lead: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
  },
  mockCard: {
    backgroundColor: ottoTokens.landingCardBackground,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: '24px',
    overflow: 'hidden',
  },
  codeBlock: {
    margin: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '20px',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: 1.7,
    overflowX: 'auto',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre',
  },
  comment: { color: ottoTokens.codeSyntaxComment },
  kw: { color: ottoTokens.codeSyntaxKeyword },
  control: { color: ottoTokens.codeSyntaxControlKeyword },
  fn: { color: ottoTokens.codeSyntaxFunction },
  str: { color: ottoTokens.codeSyntaxString },
  varName: { color: ottoTokens.codeSyntaxVariable },
});

/**
 * "Design and code" section. Two-column with the copy on the right
 * and a syntax-highlighted Python sample on the left. The token
 * colours come straight from CodeMirror's syntax tokens so this
 * matches what users actually see inside the in-product code editor.
 */
export const DesignAndCodeSection = () => {
  const styles = useStyles();

  return (
    <section className={styles.section} aria-labelledby="design-code-title">
      <div className={styles.inner}>
        <div className={styles.reverseCopy}>
          <Text className={styles.eyebrow}>Design and code</Text>
          <Text as="h2" id="design-code-title" className={styles.heading}>
            No switching between visual and code tools.
          </Text>
          <Text className={styles.lead}>
            Describe your business intent in plain words. Write code when you need more control.
            Go from idea to a running workflow with no code, or write inline JavaScript or Python
            for more power. No separate compute. No handoffs.
          </Text>
        </div>
        <div className={mergeClasses(styles.mockCard, styles.reverseVisual)}>
          <pre className={styles.codeBlock} aria-label="Inline Python code example">
            <code>
              <span className={styles.comment}>{'# Inline Python inside a workflow step'}</span>{'\n'}
              <span className={styles.control}>async def</span>{' '}
              <span className={styles.fn}>validate_submission</span>(
              <span className={styles.varName}>ctx</span>):{'\n'}
              {'    '}<span className={styles.varName}>data</span>{' = ctx.input['}
              <span className={styles.str}>&quot;spreadsheet&quot;</span>{']'}{'\n'}
              {'    '}<span className={styles.varName}>rules</span>{' = '}
              <span className={styles.control}>await</span>{' ctx.skills.'}
              <span className={styles.fn}>load</span>(
              <span className={styles.str}>&quot;sku_rules&quot;</span>){'\n'}
              {'    '}<span className={styles.varName}>result</span>{' = '}
              <span className={styles.control}>await</span>{' ctx.agent.'}
              <span className={styles.fn}>run</span>({'\n'}
              {'        instructions='}<span className={styles.str}>
                &quot;Check each row against rules&quot;
              </span>,{'\n'}
              {'        input='}<span className={styles.varName}>data</span>,{'\n'}
              {'        skills='}<span className={styles.varName}>rules</span>,{'\n'}
              {'    )\n'}
              {'    '}<span className={styles.kw}>return</span>{' { '}
              <span className={styles.str}>&quot;valid&quot;</span>{': result.passed, '}
              <span className={styles.str}>&quot;issues&quot;</span>{': result.flagged }'}
            </code>
          </pre>
        </div>
      </div>
    </section>
  );
};
